import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import TelegramIcon from "@mui/icons-material/Telegram"
import {
    AlertTitle,
    Box,
    Collapse,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    useTheme,
} from "@mui/material"
import { Dispatch, ReactElement, SetStateAction, useState } from "react"
import { FieldValues, useForm } from "react-hook-form"
import { KeyedMutator } from "swr"

import {
    NETWORK_TO_DNO_FRIENDLY_ABBREVIATION_DISPATCHER,
    NIE,
} from "../../../constants/dnos"
import { DnoCommissioningResponses } from "../../../constants/enums"
import useDnoCommissioningResponse from "../../../services/middleware/hooks/dnoInterface/protected/useDnoCommissioningResponse"
import { TGetSingleApplication } from "../../../services/middleware/hooks/dnoInterface/protected/useDnoSingleApplication"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import AppDialog from "../../dialog/appDialog"
import AppTooltip from "../../helper/appTooltip"
import ValidatedRadioGroup from "../../inputs/validated/validatedRadioGroup"
import ValidatedTextField from "../../inputs/validated/validatedTextField"
import MpanRecognisedInput from "../../submitApplication/single/recognisedInputs/mpanRecognisedInput"
import { AppCardHeader } from "../../surfaces/appCard"
import DisplayIf from "../../wrappers/displayIf"

const COMMISSIONING_RESPONSE_REG = "commissioning-response"
const MPAN_REG = "mpan"
const COMMENTS_REG = "comments"

const COMMISSIONING_DNO_RESPONSES = {
    "Commissioning Approved": DnoCommissioningResponses.Approve,
    "Commissioning Update Required": DnoCommissioningResponses.UpdateRequired,
}

interface ICommissionDecisionFormProps {
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    applicationId: string
    applicationAddress: string
    applicationPostcode: string
    applicationMpan: string | number
    applicationDno: string
    mutate: KeyedMutator<TGetSingleApplication>
}

const DnoCommissionDecisionForm = ({
    isOpen,
    applicationId,
    applicationAddress,
    setIsOpen,
    applicationPostcode,
    applicationMpan,
    applicationDno,
    mutate,
}: ICommissionDecisionFormProps): ReactElement => {
    const [mpanVisible, setMpanVisible] = useState(false)

    const theme = useTheme()

    const mpanFirstTwoDigits = applicationMpan.toString().substring(0, 2)
    const applicationIdFirstTwoDigits = applicationId.substring(0, 2)

    const defaultValues: FieldValues = {
        [MPAN_REG]: mpanFirstTwoDigits,
        [COMMISSIONING_RESPONSE_REG]: undefined,
        [COMMENTS_REG]: undefined,
    }

    const { control, handleSubmit, watch } = useForm({
        shouldUnregister: true,
        defaultValues,
    })

    const { isSending, error, postCommissioningResponse } =
        useDnoCommissioningResponse()

    const submitResponse = (): void => {
        postCommissioningResponse(
            () => {
                setIsOpen(false)
                mutate(undefined)
            },
            applicationId,
            watch(COMMISSIONING_RESPONSE_REG),
            watch(COMMENTS_REG),
            watch(MPAN_REG)
        )
    }

    const extraMpanValidation = (value: string): boolean | string => {
        const valueFirstTwoDigits = value.substring(0, 2)
        if (
            valueFirstTwoDigits === applicationIdFirstTwoDigits &&
            valueFirstTwoDigits === mpanFirstTwoDigits
        ) {
            return true
        }

        return "The first two digits of the export MPAN provided do not match the application ID and application MPAN"
    }

    const canViewMpan =
        applicationDno !== NETWORK_TO_DNO_FRIENDLY_ABBREVIATION_DISPATCHER[NIE]

    const mpanButtonVisible =
        watch(COMMISSIONING_RESPONSE_REG) === DnoCommissioningResponses.Approve
    const mpanRequired = mpanVisible && canViewMpan && mpanButtonVisible

    const maxCommentCharacters = 500

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    sx={{ p: 0 }}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={
                        <>
                            Commissioning Decision for{" "}
                            <Box
                                color={theme.palette.secondary.main}
                                display="inline"
                            >
                                {applicationAddress}
                            </Box>{" "}
                        </>
                    }
                    subheader={applicationId}
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3}>
                    <DialogContentText>
                        Commissioning information has been provided for this
                        application and is ready for your review. By accepting,
                        you are indicating you are satisfied the information is
                        accurate. Once you accept, no further changes can be
                        made to the application. If you indicate an update is
                        required, the form is sent back to the installer to edit
                        and resubmit to you.
                    </DialogContentText>
                    <DialogContentText>
                        When asking for further updates, the installer is
                        notified via email (unless your organisation has
                        disabled installer emails). When accepting, the
                        installer and the customer are notified via email
                        (unless your organisation has disabled these features).
                    </DialogContentText>
                    <Collapse in={!!error} mountOnEnter>
                        <AppAlert severity={error?.level}>
                            <AlertTitle>{error?.message}</AlertTitle>
                            {error?.displayDetail}
                        </AppAlert>
                    </Collapse>
                    <ValidatedRadioGroup
                        registerName={COMMISSIONING_RESPONSE_REG}
                        label="Choose your outcome"
                        control={control}
                        radioOptions={COMMISSIONING_DNO_RESPONSES}
                        required
                    />
                    <AppTooltip
                        title="Export MPAN cannot be provided for NIE applications"
                        visible={!canViewMpan}
                    >
                        <span>
                            <DisplayIf condition={mpanButtonVisible}>
                                <AppButton
                                    variant="outlined"
                                    disabled={!canViewMpan}
                                    onClick={() => setMpanVisible(!mpanVisible)}
                                >
                                    {mpanVisible
                                        ? "I don't want to provide export MPAN"
                                        : "I would like to provide export MPAN"}
                                </AppButton>
                            </DisplayIf>
                        </span>
                    </AppTooltip>
                    <DisplayIf condition={mpanRequired}>
                        <MpanRecognisedInput
                            registerName={MPAN_REG}
                            control={control}
                            postCode={applicationPostcode}
                            isMeterNumberEntered={false}
                            isMpanEntered={!!watch(MPAN_REG)}
                            customValidation={extraMpanValidation}
                            forceShowMpanInput={mpanRequired}
                        />
                    </DisplayIf>
                    <ValidatedTextField
                        tooltip={
                            "If your response is 'Update Required', comments will be visible to the installer via the Connect Direct portal and will be appended into an email notification (if your organisation has installer emails enabled). If your response is 'Accepted', comments will be visible to the installer in the same way. If accepting, comments will also be visible to the customer via an email notification that is sent to them (if your organisation has customer emails enabled)"
                        }
                        registerName={COMMENTS_REG}
                        required={
                            watch(COMMISSIONING_RESPONSE_REG) ===
                            DnoCommissioningResponses.UpdateRequired
                        }
                        control={control}
                        placeholder={
                            watch(COMMISSIONING_RESPONSE_REG) ===
                            DnoCommissioningResponses.UpdateRequired
                                ? "Enter your comments"
                                : "Enter any optional comments"
                        }
                        label={`Comments (${maxCommentCharacters} characters max)`}
                        customRules={{
                            required: {
                                value:
                                    watch(COMMISSIONING_RESPONSE_REG) ===
                                    DnoCommissioningResponses.UpdateRequired,
                                message:
                                    "Comments must be provided for a non-approved Commissioning response",
                            },
                            maxLength: {
                                value: maxCommentCharacters,
                                message: `Max permitted characters: 
                                ${maxCommentCharacters}`,
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    disabled={isSending}
                    onClick={() => setIsOpen(false)}
                >
                    Back
                </AppButton>
                <AppLoadingButton
                    color="success"
                    variant="contained"
                    endIcon={<TelegramIcon />}
                    loading={isSending}
                    onClick={handleSubmit(submitResponse)}
                >
                    Submit
                </AppLoadingButton>
            </DialogActions>
        </AppDialog>
    )
}

export default DnoCommissionDecisionForm
